import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, videos } from '@/mocks/videos';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'rating' | 'views' | 'latest'>('rating');

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const activeSlug = slug || 'all';

  const filteredVideos = useMemo(() => {
    let result = activeSlug === 'all' ? [...videos] : videos.filter((v) => v.category === activeSlug);

    if (sortBy === 'rating') {
      result.sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'views') {
      result.sort((a, b) => b.views - a.views);
    } else {
      result.sort((a, b) => b.year.localeCompare(a.year));
    }
    return result;
  }, [activeSlug, sortBy]);

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <div className="pt-6">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: currentCategory.name },
            ]}
          />
        </div>

        {/* Category Header */}
        <section className="w-full px-4 md:px-6 lg:px-8 py-6">
          <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
            {currentCategory.name}
          </h1>
          <p className="text-sm text-foreground-400 mt-1">
            发现更多{currentCategory.name}精彩内容，91精品视频在线播放
          </p>
        </section>

        {/* Category Tabs */}
        <section className="w-full px-4 md:px-6 lg:px-8 pb-4">
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-400 hover:text-foreground-800 border border-background-200/30'
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </Link>
            ))}
          </div>
        </section>

        {/* Sort Controls */}
        <section className="w-full px-4 md:px-6 lg:px-8 pb-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-foreground-400">排序：</span>
            {[
              { key: 'rating' as const, label: '评分最高' },
              { key: 'views' as const, label: '播放最多' },
              { key: 'latest' as const, label: '最新发布' },
            ].map((option) => (
              <button
                key={option.key}
                onClick={() => setSortBy(option.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  sortBy === option.key
                    ? 'bg-secondary-500 text-white'
                    : 'bg-background-100 text-foreground-400 hover:text-foreground-800 border border-background-200/30'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </section>

        {/* Video Grid */}
        <section className="w-full px-4 md:px-6 lg:px-8 pb-10 md:pb-14">
          {filteredVideos.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {filteredVideos.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-foreground-400">暂无内容</p>
            </div>
          )}
        </section>
      </main>

      <Footer />
    </>
  );
}